// i18next-extract-mark-ns-start Step1
/* eslint-disable react/jsx-indent */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { getTreeSofaCreations } from 'tilli-core/dist/trees/treeHelpers';
import { getRepairBonusAmountForCloth } from 'tilli-core/dist/services/repairBonus';

import { colors, tabletThresholdPixels } from '../home/v4/styledComponents';
import { formatPrice, upcyclingPricingInverse } from '../../services/pricing';

import iconEdit from '../../assets/order/iconEdit.svg';
import iconGarbage from '../../assets/order/iconGarbage.svg';
import iconWarning from '../../assets/order/warning.jpg';
import { getFabricName } from './fabrics';
import infoLogo from '../../assets/information.svg';

const TotalRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: 700;
  font-size: 16px;
  margin: 25px 0px 17px;
  text-transform: uppercase;
  ${({ grey }) => grey && `color: ${colors.grey200}; `}

  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0 10px;
    font-size: 14px;
    line-height: 17px;
  }
`;

const AlterationsRow = styled(TotalRow)`
  justify-content: ${({ spaceBetween }) => spaceBetween ? 'space-between' : 'flex-start'};
  align-items: start;
  font-weight: normal;
  font-size: 14px;
  text-transform: none;
  margin: 2.5px 0px;
  ${({ discount }) => discount && `
    background-color: ${colors.yellow900};
    font-size: 16px;
    line-height: 17px;
    font-weight: 700;
    padding: 5px 17px;
    margin: 10px -17px;
    border-radius: 4px;
  `}

  @media (max-width: ${tabletThresholdPixels}) {
    font-size: ${({ discount }) => discount ? '14px' : '12px'};
  }
`;

const IconEdit = styled.img`
  margin-left: 5px;
  cursor: pointer;
  height: 13px;
`;

const getTypeOfWorkLabel = (t, selectedTypeOfWork) => {
  if (selectedTypeOfWork === 'embroidery') return t('Broder');
  if (selectedTypeOfWork === 'repair') return t('Réparer');
  if (selectedTypeOfWork === 'alteration') return t('Retoucher');
  if (selectedTypeOfWork === 'transformation') return t('Transformer');
  if (selectedTypeOfWork === 'creation') return t('Créer');
  if (selectedTypeOfWork === 'prevent') return t('Entretenir');
  if (selectedTypeOfWork === 'cure') return t('Réparer');
  if (selectedTypeOfWork === 'recolor') return t('Recolorer');
  return '';
};

const FlexContainer = styled.div`
  display: flex;
`;

const Button = styled.div`
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 700;
  margin-top: 17px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 5px;
    font-size: 12px;
    margin-bottom: ${({ vertical }) => !vertical && 25}px;
  }
`;

const StyledReactTooltip = styled(Tooltip)`
  background-color: ${colors.black} !important;
  width: auto !important;
  height: auto !important;
`;

const TooltipText = styled.p`
  font-family: Roboto;
  font-size: 13px;
  color: ${colors.white};
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 12px;
  }
`;

const Img = styled.img`
  margin-bottom: 8px;
  margin-left: 8px;
  height: 10px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-bottom: 4px;
    margin-left: 4px;
  }
`;

const ClothTotal = styled.span`
  font-size: 16px;
`;

const renderTooltip = (t) => (
  <div>
    <StyledReactTooltip place="top" variant="light" anchorId="undefinedPrice">
      <TooltipText>{t('Estimation en RDV')}</TooltipText>
    </StyledReactTooltip>
    <Img
      src={infoLogo}
      alt={t('Pourquoi le prix est indéfini ?')}
      id="undefinedPrice"
    />
  </div>
);

const renderPriceRangeTooltip = (t, priceRange) => (
  <div>
    <StyledReactTooltip place="top" variant="light" anchorId="priceRange">
      <TooltipText>
        {t('Les prix peuvent varier à +/- {{price}}€ pour ce type de transformation.', {
          price: Math.round(priceRange / 2),
        })}
      </TooltipText>
    </StyledReactTooltip>
    <Img
      src={iconWarning}
      alt={`Les prix peuvent varier à +/- ${Math.round(priceRange
        / 2)}€ pour ce type de transformation.`}
      id="priceRange"
    />
  </div>
);

const renderPieceTotal = (t, cloth, priceRange, vertical, currency) => {
  if (cloth.total === 0) {
    return (
      <FlexContainer>
        ?
        {renderTooltip(t)}
      </FlexContainer>
    );
  }
  if (cloth.typeOfWork !== 'transformation' && cloth.hasTalkRDV) {
    return (
      <FlexContainer>
        {`${formatPrice(cloth.total, currency)} + ?`}
        {renderTooltip(t)}
      </FlexContainer>
    );
  } if (priceRange > 0) {
    return (
      <FlexContainer>
        {`${formatPrice(cloth.total, currency)}`}
        {renderPriceRangeTooltip(t, priceRange)}
      </FlexContainer>
    );
  }
  return (
    <ClothTotal vertical={vertical}>
      {`${formatPrice(cloth.total, currency)}`}
    </ClothTotal>
  );
};

function renderClothName({
  t,
  cloth,
  deleteCloth,
  priceRange,
  setEditingClothIndex,
  vertical,
  lastStep,
  currency,
}) {
  return (
    <TotalRow>
      <span>
        {t(cloth.name)}
        {!lastStep && (
          <>
            <IconEdit
              onClick={() => {
                setEditingClothIndex(cloth.index);
              }}
              src={iconEdit}
              alt={t('Éditer')}
            />
            <IconEdit
              onClick={() => deleteCloth(cloth.index, { suitPiece: cloth.suitPiece, sofaPart: cloth.sofaPart })}
              src={iconGarbage}
              alt={t('Supprimer')}
            />
          </>
        )}
      </span>
      {renderPieceTotal(t, cloth, priceRange, vertical, currency)}
    </TotalRow>
  );
}

renderClothName.propTypes = {
  cloth: PropTypes.shape({}).isRequired,
  deleteCloth: PropTypes.func.isRequired,
  setEditingClothIndex: PropTypes.func,
  priceRange: PropTypes.number,
  vertical: PropTypes.bool,
  lastStep: PropTypes.bool,
  currency: PropTypes.string.isRequired,
};

const renderItems = ({
  t, cloth, pricingInverse, currency,
}) => cloth.items.filter((item) => !item.hide).map((item, index) => ({ ...item, index })).map((item) => {
  let pricingInverseName = pricingInverse[item.id]?.name?.replace(
    ' (x1)',
    '',
  ) || item.label;
  if (item.quantity > 1) {
    pricingInverseName = `${t(pricingInverseName)} (x${item.quantity})`;
  }
  const typeOfWork = getTypeOfWorkLabel(t, cloth.typeOfWork);
  return (
    <>
      <AlterationsRow key={`${item.id}_${item.index}`} spaceBetween>
        <span>{`${t(typeOfWork)} > ${t(pricingInverseName || item.name)}`}</span>
        <span>{`${formatPrice(item.price, currency)}`}</span>
      </AlterationsRow>
      {cloth.liningsNumber > 0 && item.liningUnitPrice && (
        <AlterationsRow spaceBetween>
          <span>
            {`${t('avec doublure(s)')}`}
          </span>
          <span>{`${formatPrice(item.liningUnitPrice, currency)}`}</span>
        </AlterationsRow>
      )}
    </>
  );
});

const renderSofaPart = ({ t, cloth }) => (
  cloth.sofaPartSubTypes && (
    <AlterationsRow key={cloth.slug}>
      <span>
        {getTreeSofaCreations(t).parts[cloth.slug].labelSummary}
        {cloth.items[0].quantity > 1 ? ` (x${cloth.items[0].quantity})` : ''}
      </span>
    </AlterationsRow>
  )
);

const renderSofaPartSubTypes = ({ t, cloth }) => Object.entries(cloth.sofaPartSubTypes || []).map(([key, subType]) => {
  let name = getTreeSofaCreations(t).parts[cloth.slug].subTypeQuestions.find(({ slug }) => slug === key).answers.find(({ slug }) => slug === subType).labelSummary;
  if (cloth.items[0].quantity > 1) {
    name = `${name} (x${cloth.items[0].quantity})`;
  }
  return (
    <AlterationsRow key={`${key}`}>
      <span>{name}</span>
    </AlterationsRow>
  );
});

const renderUpcyclingItems = ({ cloth }) => cloth.upcyclingItems.map((item) => {
  let pricingInverseName = upcyclingPricingInverse[item.id].name.replace(
    ' (x1)',
    '',
  );
  if (item.quantity && item.quantity > 1) {
    pricingInverseName = `${pricingInverseName} (x${item.quantity})`;
  }
  return (
    <AlterationsRow key={item.id}>
      <span>{pricingInverseName}</span>
    </AlterationsRow>
  );
});

const renderSuitAlterations = ({ cloth }) => cloth.suitAlterations?.map((suitAlteration) => (
  <AlterationsRow key={suitAlteration.label}>
    <span>{suitAlteration.label}</span>
  </AlterationsRow>
));

const renderFabrics = ({ t, cloth }) => (
  <AlterationsRow key={cloth.fabric}>
    <span>{getFabricName(t, cloth)}</span>
  </AlterationsRow>
);

const renderFabricsWedding = ({ t, cloth }) => cloth.name === 'Robe de mariée' && (
  <AlterationsRow>
    <span>{t('Dentelle et soie / mousseline')}</span>
  </AlterationsRow>
);

const renderFabricAccessoriesCreation = ({ t, cloth }) => cloth.slug?.includes('crea_') && (
  <AlterationsRow>
    <span>{t('Tissu non fourni')}</span>
  </AlterationsRow>
);

const renderRepairBonus = ({ t, cloth, discounts }) => {
  const repairBonusDiscount = discounts.find((discountIt) => discountIt.name === 'Bonus Réparation');
  if (!repairBonusDiscount) return null;
  const amount = getRepairBonusAmountForCloth(cloth, repairBonusDiscount);
  if (!amount) return null;
  return (
    <AlterationsRow spaceBetween discount>
      <span>{t('Bonus réparation')}</span>
      <span>{`-${amount}€`}</span>
    </AlterationsRow>
  );
};

function renderDuplicateButton({
  t, cloth, clothes, duplicateCloth, lastStep,
}) {
  return !lastStep ? (
    <Button
      onClick={() => duplicateCloth(
        cloth.index,
        cloth.isCurrent && clothes[clothes.length - 1],
        { suitPiece: cloth.suitPiece, sofaPart: cloth.sofaPart },
      )}
    >
      {t('Dupliquer')}
    </Button>
  ) : null;
}

renderDuplicateButton.propTypes = {
  clothes: PropTypes.arrayOf(PropTypes.shape({})),
  cloth: PropTypes.shape({}).isRequired,
  duplicateCloth: PropTypes.func.isRequired,
  lastStep: PropTypes.bool,
};

function OrderSummaryCloth({
  cloth,
  clothes,
  deleteCloth,
  duplicateCloth,
  editingClothIndex,
  priceRange,
  setEditingClothIndex,
  pricingInverse,
  vertical,
  lastStep,
  discounts,
  currency,
}) {
  const { t } = useTranslation();
  return (
    <>
      {renderClothName({
        t,
        cloth,
        clothes,
        deleteCloth,
        editingClothIndex,
        priceRange,
        setEditingClothIndex,
        vertical,
        lastStep,
        currency,
      })}
      {renderSofaPart({ t, cloth })}
      {renderSofaPartSubTypes({ t, cloth })}
      {renderItems({
        t, cloth, pricingInverse, currency,
      })}
      {renderUpcyclingItems({ cloth })}
      {renderSuitAlterations({ cloth })}
      {renderFabrics({ t, cloth })}
      {renderFabricsWedding({ t, cloth })}
      {renderFabricAccessoriesCreation({ t, cloth })}
      {renderRepairBonus({ t, cloth, discounts })}
      {renderDuplicateButton({
        t, cloth, clothes, duplicateCloth, lastStep,
      })}
    </>
  );
}

OrderSummaryCloth.propTypes = {
  clothes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          slug: PropTypes.string,
        }),
      ),
      typeOfWork: PropTypes.string,
      total: PropTypes.number,
      fabrics: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  cloth: PropTypes.shape({}).isRequired,
  deleteCloth: PropTypes.func,
  duplicateCloth: PropTypes.func,
  editingClothIndex: PropTypes.number,
  setEditingClothIndex: PropTypes.func,
  priceRange: PropTypes.number,
  pricingInverse: PropTypes.shape({}).isRequired,
  vertical: PropTypes.bool,
  lastStep: PropTypes.bool,
  discounts: PropTypes.arrayOf(PropTypes.shape({})),
  currency: PropTypes.string.isRequired,
};

OrderSummaryCloth.defaultProps = {
  clothes: [],
  deleteCloth() {},
  duplicateCloth() {},
  editingClothIndex: null,
  setEditingClothIndex() {},
  priceRange: 0,
  vertical: false,
  lastStep: false,
  discounts: [],
};

export default OrderSummaryCloth;
